import React, { useEffect, useState, useCallback } from 'react';
import { ShoppingCart, X, Clock, AlertTriangle, Users, Calendar, Package } from 'lucide-react';
import {
  getItinerary, removeFromItinerary, msRemaining, findOverlaps, payForItem,
} from '../utils/itinerary';

const fmtCountdown = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const fmtDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

/**
 * Floating marketplace cart — appears when the shopper has "Add to Cart &
 * Keep Browsing" items. Opens a drawer with the held experiences and starts
 * the chained per-operator checkout.
 */
export const ItineraryButton = () => {
  const [items, setItems] = useState(getItinerary());
  const [open, setOpen] = useState(false);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(false);
  const [, setTick] = useState(0);

  const refresh = useCallback(() => setItems(getItinerary()), []);

  useEffect(() => {
    window.addEventListener('bf-itinerary-changed', refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener('bf-itinerary-changed', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, [refresh]);

  // "Added to cart" toast when the widget bounces the shopper back here
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('cart_added') === '1') {
      setToast(true);
      params.delete('cart_added');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    }
  }, []);

  // Auto-dismiss with fade (separate effect so StrictMode double-mounts can't strand it)
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(false), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // If the cart empties (cleared/expired), the toast has nothing to announce
  useEffect(() => {
    if (toast && items.length === 0) setToast(false);
  }, [toast, items.length]);

  // 1s tick — live countdowns + auto-prune expired items
  useEffect(() => {
    if (items.length === 0) return;
    const t = setInterval(() => { setTick((n) => n + 1); refresh(); }, 1000);
    return () => clearInterval(t);
  }, [items.length, refresh]);

  const overlaps = findOverlaps(items);
  const soonest = items.length ? Math.min(...items.map(msRemaining)) : 0;

  const startCheckout = async () => {
    if (!items.length || paying) return;
    setPaying(true);
    setError('');
    try {
      await payForItem(items[0]);
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
      setPaying(false);
    }
  };

  const totalsBySymbol = items.reduce((acc, i) => {
    const sym = i.display?.symbol || '$';
    acc[sym] = (acc[sym] || 0) + (Number(i.display?.total) || 0);
    return acc;
  }, {});
  const totalLabel = Object.entries(totalsBySymbol)
    .map(([sym, amt]) => `${sym}${amt.toFixed(2)}`)
    .join(' + ');

  return (
    <>
      {toast && (
        <div
          data-testid="cart-added-toast"
          className="fixed top-5 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2.5 px-5 py-3 rounded-full bg-gray-900 text-white text-sm font-medium shadow-2xl animate-in fade-in slide-in-from-top-3 duration-300"
        >
          <ShoppingCart className="w-4 h-4 text-blue-300" />
          Added to your cart — held for 10 minutes
        </div>
      )}

      {items.length > 0 && (
        <button
          onClick={() => setOpen(true)}
          data-testid="itinerary-float-btn"
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-5 py-3 rounded-full bg-blue-600 text-white font-medium shadow-2xl hover:bg-blue-700 hover:-translate-y-0.5 transition-all duration-200"
        >
          <ShoppingCart className="w-4 h-4" />
          <span>Cart</span>
          <span className="flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-white text-blue-600 text-xs font-bold" data-testid="itinerary-count">
            {items.length}
          </span>
          <span className="text-xs text-blue-100 font-mono" data-testid="cart-countdown">{fmtCountdown(soonest)}</span>
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-[70]" data-testid="cart-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Your Cart</h2>
                <p className="text-xs text-gray-500">Held for 10 minutes · no payment yet</p>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="cart-drawer-close"
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              {items.map((item, idx) => {
                const d = item.display || {};
                return (
                  <div
                    key={item.id}
                    data-testid={`cart-item-${idx}`}
                    className="bg-gray-50 rounded-2xl p-3.5 flex gap-3"
                  >
                    {d.image ? (
                      <img src={d.image} alt={d.serviceName} className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-16 h-16 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{d.serviceName}</p>
                          <p className="text-xs text-gray-500 truncate">{d.orgName}</p>
                          {d.isPackage && (
                            <span className="inline-flex items-center gap-1 mt-1 rounded-full bg-blue-50 text-blue-700 text-[10px] font-bold px-2 py-0.5" data-testid={`cart-item-package-chip-${idx}`}>
                              <Package className="w-2.5 h-2.5" /> Package · {d.itemCount} experiences
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => { removeFromItinerary(item.id); }}
                          data-testid={`cart-item-remove-${idx}`}
                          className="p-1 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 flex-shrink-0"
                          title="Remove"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-600">
                        <span>{fmtDate(d.date)} · {d.displayTime}</span>
                        <span className="flex items-center gap-1"><Users className="w-3 h-3" />{d.guests}</span>
                      </div>
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="flex items-center gap-1 text-xs text-amber-600 font-medium">
                          <Clock className="w-3 h-3" />{fmtCountdown(msRemaining(item))}
                        </span>
                        <span className="text-sm font-bold text-gray-900">{d.symbol}{Number(d.total || 0).toFixed(2)}</span>
                      </div>
                      {overlaps.has(item.id) && (
                        <p className="flex items-center gap-1 mt-1.5 text-[11px] text-amber-700 bg-amber-50 rounded-lg px-2 py-1">
                          <AlertTriangle className="w-3 h-3 flex-shrink-0" /> Overlaps with another booking — that's fine if intentional
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
              {items.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-10">Your cart is empty.</p>
              )}
            </div>

            {items.length > 0 && (
              <div className="px-5 py-4 space-y-3 bg-gray-50/60">
                {error && (
                  <p data-testid="cart-checkout-error" className="text-xs text-red-700 bg-red-50 rounded-lg px-3 py-2">{error}</p>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Total ({items.length} {items.length === 1 ? 'experience' : 'experiences'})</span>
                  <span className="text-lg font-bold text-gray-900" data-testid="cart-total">{totalLabel}</span>
                </div>
                <button
                  onClick={startCheckout}
                  disabled={paying}
                  data-testid="cart-complete-booking-btn"
                  className="w-full py-3.5 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors disabled:opacity-60"
                >
                  {paying ? 'Starting secure checkout…' : (items.length === 1 ? 'Complete Booking' : `Complete Booking — pay 1 of ${items.length}`)}
                </button>
                <p className="text-[11px] text-gray-400 text-center leading-relaxed">
                  Each experience is paid securely to its operator. After each payment you'll be brought right back to pay the next one.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ItineraryButton;
